import PageLayout from "pages/layout/PageLayout"
import { useCallback, useEffect, useState } from "react"

import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import { TippyCustomzie } from "components/TippyCustomzie"
import { ReactComponent as IconActionThreeDot } from "assets/images/icons/icon-three-dots.svg"
import ButtonHasIcon from "components/Button/ButtonHasIcon"
import { cloneDeep, debounce, includes } from "lodash"
import ActionItem from "pages/projects/molecules/ActionItem"
import { Pagination } from "components/Pagination/Pagination"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import InputDefault from "components/Input/InputDefault"
import { ReactComponent as IconClearSearch } from "assets/images/icons/icon-clear-search.svg"
import { useAppSelector } from "hooks/useApp"
import { PATH } from "constants/path"
import { pushTo } from "helpers/history"
import { hasPermission } from "helpers/permissions"
import { PERMISSIONS } from "constants/permissions"
import { FormGroupsModal } from "./Modal/FormGroupsModal"
import {
  deleteGroupsOrganizationMiddleware,
  getGroupsOrganizationMiddleware,
} from "./groups.api"
import {
  GroupsOrganizationQueryState,
  GroupsOrganizationResponse,
} from "./groups.type"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"

interface Props {
  isAddGroup?: boolean
}
export const GroupsPage = (props: Props) => {
  const { isAddGroup } = props
  const userInfo = useAppSelector((state) => state.userInfo)
  const { isMobile } = useWindowSize()
  const [groups, setGroups] = useState<GroupsOrganizationResponse[]>([])
  const [showTippy, setShowTippy] = useState([] as string[])
  const openModalForm = useBoolean()
  const [dataSelected, setDataSelected] = useState<GroupsOrganizationResponse>({
    id: "",
    name: "",
    description: "",
  })

  const [queryState, setQueryState] = useState<GroupsOrganizationQueryState>({
    page: 1,
    pageSize: 25,
    search: "",
  })
  const totalPage = useNumber(0)
  const search = useString()

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }

    if (!hasPermission(userInfo, PERMISSIONS.GROUP_ORGANIZATION_VIEW)) {
      pushTo(PATH.notFound)
      return
    }
    if (
      hasPermission(userInfo, PERMISSIONS.GROUP_ORGANIZATION_VIEW) &&
      hasPermission(userInfo, PERMISSIONS.USER_VIEW) &&
      !isAddGroup
    ) {
      pushTo(PATH.userGroups, undefined, undefined, { tab: "Groups" })
    }
  }, [userInfo?.id, isAddGroup])

  useEffect(() => {
    getGroups(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState.pageSize, queryState.page, queryState.search])

  const getGroups = async (newQueyState: GroupsOrganizationQueryState) => {
    const response = await getGroupsOrganizationMiddleware(newQueyState)
    setGroups(response.data)
    totalPage.setValue(response.total)
  }

  const handleClickTippy = (value) => {
    if (showTippy.includes(value)) {
      setShowTippy(showTippy.filter((item) => item !== value))
    } else setShowTippy(showTippy.concat([value]))
  }

  const handleRowClick = (group: GroupsOrganizationResponse) => {
    pushTo(`/groups/${group.id}`)
  }

  const handleWhenSubmit = (response: GroupsOrganizationResponse) => {
    openModalForm.setValue(false)
    setDataSelected({
      id: "",
      name: "",
      description: "",
    })
    if (dataSelected.id) {
      const newGroups = cloneDeep(groups)
      const index = newGroups.findIndex((item) => item.id === response.id)
      if (index > -1) {
        newGroups[index] = response
      }
      setGroups(newGroups)
    } else {
      if (queryState.page === 1) {
        getGroups(queryState)
      } else {
        setQueryState({
          ...queryState,
          page: 1,
        })
      }
    }
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }
  const onPreventBubble = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce(
      (newSearch: string, newQueyState: GroupsOrganizationQueryState) => {
        setQueryState({
          ...newQueyState,
          search: newSearch.trim(),
          page: 1,
        })
      },
      500
    ),
    []
  )

  const handleDeleteGroup = (groupId: string) => {
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: <p>This action cannot be undone.</p>,
            label: `Warning`,
            content: `Press "Delete" to process`,
            onSubmit: async () => {
              try {
                await deleteGroupsOrganizationMiddleware(groupId)
                if (queryState.page === 1) {
                  getGroups(queryState)
                } else {
                  setQueryState({
                    ...queryState,
                    page: 1,
                  })
                }
                closeModal()
                toast(
                  <LabelNotificationPage
                    messenger="Group deleted successfully!"
                    type="success"
                  />
                )
              } catch (error: any) {
                closeModal()
                toast(
                  <LabelNotificationPage
                    messenger="Group deleted failed!"
                    type="error"
                  />
                )
              }
            },
            styleTitle: { textAlign: "center" },
            titleButton: "Delete",
          },
        },
      })
    )
  }

  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Group ID",
      dataIndex: "code",
      render: (columnData: GroupsOrganizationResponse) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <p className="font-semibold text-[13px] leading-[22px] text-[#111111]">
              {`${columnData.code} - ${columnData.name}`}
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Project Count:{" "}
              <span className="font-semibold">
                {columnData.projectCounts || 0}
              </span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Member Count:{" "}
              <span className="font-semibold">
                {columnData.memberCounts || 0}
              </span>
            </p>
            {columnData.description ? (
              <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                {columnData.description}
              </p>
            ) : null}
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "Group ID",
      dataIndex: "description",
      width: "100px",
      render: (columnData: GroupsOrganizationResponse) => (
        <p className="pl-[20px]">{columnData.code}</p>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (columnData: GroupsOrganizationResponse) => (
        <p>{columnData.name}</p>
      ),
      width: `30%`,
    },

    {
      title: "Description",
      dataIndex: "description",
      width: `30%`,
      render: (columnData: GroupsOrganizationResponse) => (
        <p>{columnData.description}</p>
      ),
    },
    {
      title: "Project Count",
      dataIndex: "projectCounts",
      width: `15%`,
      render: (columnData: GroupsOrganizationResponse) => (
        <span className="text-blue-600 font-medium">
          {columnData.projectCounts || 0}
        </span>
      ),
    },
    {
      title: "Member Count",
      dataIndex: "memberCounts",
      width: `15%`,
      render: (columnData: GroupsOrganizationResponse) => (
        <span className="text-green-600 font-medium">
          {columnData.memberCounts || 0}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: `10%`,
      render: (columnData: GroupsOrganizationResponse) => {
        if (isMobile || !isAddGroup) {
          return null
        }
        const canEdit = (columnData.groupPermissions || []).includes(
          PERMISSIONS.GROUP_ORGANIZATION_EDIT
        )
        const canDelete =
          hasPermission(userInfo, PERMISSIONS.GROUP_ORGANIZATION_DELETE) &&
          !columnData.memberCounts &&
          !columnData.projectCounts
        const isAction = canEdit || canDelete
        if (!isAction) {
          return null
        }

        return (
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showTippy.includes(columnData.id)}
            onClickOutside={() =>
              setShowTippy(showTippy.filter((item) => item !== columnData.id))
            }
            content={
              <div
                onClick={onPreventBubble}
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  width: 120,
                }}
              >
                {canEdit ? (
                  <ActionItem
                    title="Edit"
                    onClick={() => {
                      setDataSelected(columnData)
                      openModalForm.setValue(true)
                      handleClickTippy(columnData.id)
                    }}
                  />
                ) : null}
                {canDelete ? (
                  <ActionItem
                    title="Delete"
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      handleDeleteGroup(columnData.id)
                    }}
                  />
                ) : null}
              </div>
            }
            allowHTML
          >
            <div
              className="flex items-center justify-end cursor-pointer"
              onClick={(e) => {
                onPreventBubble(e)
                handleClickTippy(columnData.id)
              }}
            >
              <IconActionThreeDot />
            </div>
          </TippyCustomzie>
        )
      },
    },
  ]

  if (isAddGroup) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
          <h2 className="text-[16px] font-semibold lh-26 text-black">Groups</h2>
          <div className="flex items-center gap-[12px]">
            {!isMobile ? (
              <div
                className=" bg-grayWhite2 p-2 rounded-full flex items-center"
                style={{
                  width: isMobile ? 200 : 320,
                }}
                onClick={onPreventBubble}
              >
                <IconSearchNormal
                  style={{
                    width: 20,
                  }}
                  values={search.value}
                  onClick={onPreventBubble}
                  onChange={(e) => {
                    onPreventBubble(e)
                  }}
                />
                <InputDefault
                  value={search.value}
                  placeholder="Search by group name"
                  style={{
                    border: "none",
                    boxShadow: "none",
                    height: 30,
                    fontWeight: 400,
                  }}
                  classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
                  onClick={onPreventBubble}
                  onChange={(e) => {
                    onPreventBubble(e)
                    search.setValue(e.target.value)
                    debounceSearch(e.target.value, cloneDeep(queryState))
                  }}
                />
                {search.value ? (
                  <div
                    onClick={(e) => {
                      onPreventBubble(e)
                      search.setValue("")
                      setQueryState({
                        ...queryState,
                        search: "",
                      })
                    }}
                    className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
                  >
                    <IconClearSearch />
                  </div>
                ) : null}
              </div>
            ) : null}
            {!isMobile &&
            hasPermission(userInfo, PERMISSIONS.GROUP_ORGANIZATION_ADD) ? (
              <ButtonHasIcon
                title="New group"
                widthButton={130}
                onClick={() => {
                  openModalForm.setValue(true)
                  setDataSelected({
                    id: "",
                    name: "",
                    description: "",
                  })
                }}
              />
            ) : null}
          </div>
        </div>
        <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
          <div className="flex flex-col pr-6 overflow-auto">
            <div className="text-black bg-white border border-border-gray rounded-md custom-table pb-[6px]">
              <SimpleTable
                customClass="storage-usage-table"
                columns={isMobile ? columnsMobile : columns}
                dataSource={groups || []}
                onRowClick={handleRowClick}
                isRequiredTableHeader={isMobile ? false : true}
              />
            </div>
            {totalPage.value ? (
              <Pagination
                total={totalPage.value}
                queryState={{
                  page: queryState.page,
                  limit: queryState.pageSize,
                }}
                handleChangePage={handleChangePage}
              />
            ) : null}
          </div>
        </div>
        {openModalForm.value ? (
          <FormGroupsModal
            openModal={openModalForm.value}
            onCloseModal={() => {
              openModalForm.setValue(false)
              setDataSelected({
                id: "",
                name: "",
                description: "",
              })
            }}
            dataSelected={dataSelected}
            handleWhenSubmit={handleWhenSubmit}
          />
        ) : null}
      </div>
    )
  }

  return (
    <PageLayout
      childrenHeader={
        <div className="flex items-center justify-between h-full px-[24px]">
          <h2 className="text-[16px] font-semibold lh-26 text-black">Groups</h2>
          <div className="flex items-center gap-[12px]">
            <div
              className=" bg-grayWhite2 p-2 rounded-full flex items-center"
              style={{
                width: isMobile ? 200 : 320,
              }}
              onClick={onPreventBubble}
            >
              <IconSearchNormal
                style={{
                  width: 20,
                }}
                values={search.value}
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                }}
              />
              <InputDefault
                value={search.value}
                placeholder="Search by group name"
                style={{
                  border: "none",
                  boxShadow: "none",
                  height: 30,
                  fontWeight: 400,
                }}
                classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                  search.setValue(e.target.value)
                  debounceSearch(e.target.value, cloneDeep(queryState))
                }}
              />
              {search.value ? (
                <div
                  onClick={(e) => {
                    onPreventBubble(e)
                    search.setValue("")
                    setQueryState({
                      ...queryState,
                      search: "",
                    })
                  }}
                  className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
                >
                  <IconClearSearch />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col p-6">
        <div className="text-black bg-white border border-border-gray rounded-md overflow-auto">
          <SimpleTable
            customClass="storage-usage-table"
            columns={isMobile ? columnsMobile : columns}
            dataSource={groups || []}
            onRowClick={handleRowClick}
            isRequiredTableHeader={isMobile ? false : true}
          />
        </div>
        {totalPage.value ? (
          <Pagination
            total={totalPage.value}
            queryState={{
              page: queryState.page,
              limit: queryState.pageSize,
            }}
            handleChangePage={handleChangePage}
          />
        ) : null}
      </div>
    </PageLayout>
  )
}
